<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">DCMM认证指南</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">数据治理知识库</span>
          <span class="tag">DCMM</span>
          <span class="tag">认证指南</span>
        </div>
        <h1 class="article-title">DCMM认证指南</h1>
        <p class="article-subtitle">数据管理能力成熟度模型 · 国家标准解读 · 认证流程 · 评估工具 · 实战案例</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-01-15</span>
          <span><i class="far fa-clock"></i> 阅读约 18 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">📖</div>
        <div>
          <h3>导读</h3>
          <p>DCMM（Data management Capability Maturity Model）是我国首个数据管理领域的国家标准（GB/T 36073-2018），为企业衡量和提升数据管理能力提供了权威框架。本指南从标准解读、认证流程、评估工具到实战案例，全方位帮助企业了解并顺利通过DCMM认证。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、什么是DCMM</a></li>
          <li><a href="#section2">二、五级成熟度模型详解</a></li>
          <li><a href="#section3">三、八大能力域解析</a></li>
          <li><a href="#section4">四、认证流程与申报材料</a></li>
          <li><a href="#section5">五、评估工具与自测方法</a></li>
          <li><a href="#section6">六、典型企业认证案例</a></li>
          <li><a href="#section7">七、认证准备策略与建议</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、什么是DCMM</h2>
          <p>DCMM（数据管理能力成熟度评估模型）是中国信息通信研究院牵头制定的国家标准，于2018年正式发布。它提供了一套系统的数据管理能力评估框架，帮助组织识别数据管理的现状、差距和改进方向。</p>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🎯</div>
              <div>
                <h4>DCMM的核心价值</h4>
                <p>为企业提供一把"标尺"——通过标准化的评估体系，客观衡量数据管理能力水平，明确改进路径，推动数据驱动型组织建设。</p>
              </div>
            </div>
          </div>

          <h3>为什么DCMM认证越来越重要？</h3>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">国务院</div>
              <div class="stat-label">《"十四五"数字经济发展规划》明确提出推进DCMM贯标</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">17省市</div>
              <div class="stat-label">出台DCMM贯标补贴和奖励政策</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">央企考核</div>
              <div class="stat-label">国资委将数据管理水平纳入央企数字化转型评估</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">800+</div>
              <div class="stat-label">家企业已通过DCMM认证</div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、五级成熟度模型详解</h2>
          <p>DCMM将数据管理能力分为五个等级，从低到高分别为：初始级、受管理级、稳健级、量化管理级和优化级。每个等级都有明确的能力要求和特征描述。</p>

          <div class="maturity-levels">
            <div class="maturity-card" v-for="level in maturityLevels" :key="level.level" :style="{ borderLeftColor: level.color }">
              <div class="maturity-level-badge" :style="{ background: level.color }">{{ level.level }}</div>
              <div class="maturity-info">
                <h4>{{ level.name }}</h4>
                <p class="maturity-desc">{{ level.desc }}</p>
                <div class="maturity-features">
                  <span v-for="(f, i) in level.features" :key="i" class="maturity-feature">{{ f }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>目前国内企业的典型分布</h4>
              <p>约65%的企业处于1-2级水平，25%达到3级，8%达到4级，仅2%的标杆企业达到5级。央企和大型金融机构是DCMM 4级及以上的主力。</p>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、八大能力域解析</h2>
          <p>DCMM从八个能力域全面评估组织的数据管理能力，每个能力域下又细分为若干能力项，共计28个过程域。</p>

          <div class="domain-grid">
            <div class="domain-card" v-for="domain in domains" :key="domain.name">
              <div class="domain-icon">{{ domain.icon }}</div>
              <h4>{{ domain.name }}</h4>
              <p class="domain-desc">{{ domain.desc }}</p>
              <div class="domain-items">
                <span v-for="(item, i) in domain.items" :key="i">{{ item }}</span>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📊</div>
              <div>
                <h4>评估重点提示</h4>
                <p>八大能力域中，"数据质量管理"和"数据标准"是被评估企业扣分最多的领域，建议优先投入资源。"数据战略"虽然分值占比不高，但直接影响评估组对企业数据管理成熟度的整体判断。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、认证流程与申报材料</h2>

          <h3>4.1 认证流程概览</h3>
          <div class="process-flow">
            <div class="process-step" v-for="(step, i) in certProcess" :key="i">
              <div class="process-number" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="process-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
                <div class="process-duration">⏱ {{ step.duration }}</div>
              </div>
            </div>
          </div>

          <h3>4.2 申报材料清单</h3>
          <div class="material-list">
            <div class="material-category" v-for="cat in materials" :key="cat.category">
              <h4>{{ cat.category }}</h4>
              <ul>
                <li v-for="(item, i) in cat.items" :key="i">{{ item }}</li>
              </ul>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">⚠️</div>
            <div>
              <h4>常见申报失误</h4>
              <p>①证据材料只有制度文件没有执行记录；②数据标准覆盖面不足（仅覆盖核心系统）；③缺乏量化的数据质量指标；④组织架构中没有明确的数据治理岗位设置。提前规避这些问题，可大幅提升通过率。</p>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、评估工具与自测方法</h2>

          <h3>5.1 DCMM自测评估表</h3>
          <p>在正式申报前，建议企业先进行内部自测，客观评估当前数据管理成熟度水平。以下是自测评估的核心维度：</p>

          <div class="assessment-table">
            <table>
              <thead>
                <tr>
                  <th>能力域</th>
                  <th>评估要点</th>
                  <th>1级标准</th>
                  <th>3级标准</th>
                  <th>5级标准</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assessmentItems" :key="item.domain">
                  <td class="domain-cell">{{ item.domain }}</td>
                  <td>{{ item.point }}</td>
                  <td>{{ item.level1 }}</td>
                  <td>{{ item.level3 }}</td>
                  <td>{{ item.level5 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>5.2 评估工具推荐</h3>
          <div class="tool-grid">
            <div class="tool-card">
              <div class="tool-icon">📝</div>
              <h4>评估问卷模板</h4>
              <p>包含28个过程域、200+评估题目的标准化问卷，可直接用于内部评估。</p>
            </div>
            <div class="tool-card">
              <div class="tool-icon">📊</div>
              <h4>雷达图生成器</h4>
              <p>输入各能力域得分，自动生成八维雷达图，直观展现能力短板。</p>
            </div>
            <div class="tool-card">
              <div class="tool-icon">📋</div>
              <h4>差距分析矩阵</h4>
              <p>对标目标等级，自动识别差距项并生成改进优先级排序。</p>
            </div>
            <div class="tool-card">
              <div class="tool-icon">📁</div>
              <h4>材料准备Checklist</h4>
              <p>按能力域分类的材料需求清单，逐项勾选确认，避免遗漏。</p>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、典型企业认证案例</h2>

          <div class="case-cards">
            <div class="case-card" v-for="c in cases" :key="c.name">
              <div class="case-header" :style="{ background: c.color }">
                <div class="case-level">DCMM {{ c.level }}级</div>
                <h3>{{ c.name }}</h3>
                <p class="case-industry">{{ c.industry }}</p>
              </div>
              <div class="case-body">
                <div class="case-metric">
                  <span class="metric-label">认证周期</span>
                  <span class="metric-value">{{ c.duration }}</span>
                </div>
                <div class="case-metric">
                  <span class="metric-label">投入人力</span>
                  <span class="metric-value">{{ c.team }}</span>
                </div>
                <h4>主要做法</h4>
                <ul>
                  <li v-for="(p, i) in c.practices" :key="i">{{ p }}</li>
                </ul>
                <h4>关键成果</h4>
                <p class="case-result">{{ c.result }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、认证准备策略与建议</h2>

          <h3>7.1 分阶段准备路线图</h3>
          <div class="roadmap">
            <div class="roadmap-phase" v-for="(phase, i) in roadmap" :key="i">
              <div class="roadmap-marker" :style="{ background: phase.color }">{{ phase.stage }}</div>
              <div class="roadmap-content">
                <h4>{{ phase.title }}</h4>
                <p>{{ phase.desc }}</p>
                <div class="roadmap-tasks">
                  <span v-for="(task, j) in phase.tasks" :key="j">{{ task }}</span>
                </div>
                <div class="roadmap-duration">⏱ {{ phase.duration }}</div>
              </div>
            </div>
          </div>

          <h3>7.2 古杉数据DCMM咨询服务</h3>
          <div class="service-grid">
            <div class="service-card">
              <div class="service-icon">🔍</div>
              <h4>现状评估</h4>
              <p>对标DCMM标准进行全维度诊断，输出差距分析报告与改进建议</p>
            </div>
            <div class="service-card">
              <div class="service-icon">📐</div>
              <h4>体系建设</h4>
              <p>协助制定数据管理制度体系、标准规范、流程设计</p>
            </div>
            <div class="service-card">
              <div class="service-icon">🛠️</div>
              <h4>工具落地</h4>
              <p>部署数据标准管理、数据质量监控等工具平台</p>
            </div>
            <div class="service-card">
              <div class="service-icon">🎯</div>
              <h4>认证辅导</h4>
              <p>全程陪跑认证申报流程，辅导材料准备与现场评审</p>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语</h4>
                <p>DCMM认证不是"考试"，而是企业数据管理能力建设的一次系统性体检和升级。通过认证的过程本身，就是推动组织建立规范化数据管理体系的最佳契机。提前规划、系统准备、持续优化——这是通过DCMM认证的三大关键。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>需要DCMM认证咨询？</h2>
        <p>古杉数据已辅导多家央企、上市公司通过DCMM 3级/4级认证</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约DCMM咨询</router-link>
          <router-link to="/consulting/data-governance" class="btn-outline">了解数据治理咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'DCMMGuide',
  data() {
    return {
      maturityLevels: [
        { level: '1级', name: '初始级', color: '#9CA3AF', desc: '数据管理处于无序状态，缺乏制度和流程，依赖个人经验。', features: ['无正式制度', '临时性管理', '数据孤岛严重'] },
        { level: '2级', name: '受管理级', color: '#F59E0B', desc: '组织已意识到数据管理的重要性，开始建立基本制度和流程。', features: ['初步制度', '部分标准', '局部推进'] },
        { level: '3级', name: '稳健级', color: '#3B82F6', desc: '数据管理制度和流程体系化覆盖，组织架构和职责明确。', features: ['全面制度', '标准规范', '组织保障'] },
        { level: '4级', name: '量化管理级', color: '#10B981', desc: '数据管理过程可量化、可度量，质量指标体系完善。', features: ['量化度量', '持续监控', '数据驱动'] },
        { level: '5级', name: '优化级', color: '#8B5CF6', desc: '数据管理持续自我优化，以数据创新驱动业务发展。', features: ['持续优化', '创新驱动', '行业标杆'] }
      ],
      domains: [
        { icon: '🎯', name: '数据战略', desc: '组织数据管理的战略规划与顶层设计', items: ['数据战略规划', '数据战略实施', '数据战略评估'] },
        { icon: '🏢', name: '数据治理', desc: '数据管理的组织架构、制度、流程', items: ['治理组织', '治理制度', '治理沟通'] },
        { icon: '📏', name: '数据标准', desc: '数据标准的制定、发布与执行', items: ['业务术语', '参考数据', '主数据', '数据元'] },
        { icon: '✅', name: '数据质量', desc: '数据质量的度量、监控、改进', items: ['质量需求', '质量检查', '质量分析', '质量提升'] },
        { icon: '🔄', name: '数据应用', desc: '数据分析、共享与服务', items: ['数据分析', '数据开放', '数据服务'] },
        { icon: '🔐', name: '数据安全', desc: '数据安全策略和隐私保护', items: ['安全策略', '安全管理', '隐私保护'] },
        { icon: '🏗️', name: '数据架构', desc: '数据架构的规划与实施', items: ['架构模型', '数据分布', '数据集成'] },
        { icon: '💰', name: '数据生存周期', desc: '数据从创建到销毁的全周期管理', items: ['数据需求', '数据设计', '数据存储', '数据归档'] }
      ],
      certProcess: [
        { title: '内部自评', desc: '组织内部对标DCMM标准进行自我评估，识别差距', duration: '2-4周', color: '#4A90D9' },
        { title: '差距整改', desc: '根据自评结果制定整改方案，补齐制度流程和工具能力', duration: '2-6个月', color: '#1F6FEB' },
        { title: '材料准备', desc: '整理评估证据材料，编写评估申请文档', duration: '4-8周', color: '#0E4EAD' },
        { title: '正式申报', desc: '向认证机构提交申报材料，等待审核', duration: '2-4周', color: '#0A3A82' },
        { title: '现场评审', desc: '评审专家现场访谈、文件审查、证据验证', duration: '3-5天', color: '#0E1F52' },
        { title: '结果发布', desc: '评审机构出具评估报告，颁发等级证书', duration: '2-4周', color: '#059669' }
      ],
      materials: [
        { category: '📋 组织管理类', items: ['数据管理组织架构图', '数据管理岗位职责说明', '数据治理委员会章程', '数据管理工作会议纪要'] },
        { category: '📝 制度规范类', items: ['数据管理总纲/办法', '数据标准管理制度', '数据质量管理制度', '数据安全管理制度', '元数据管理规范'] },
        { category: '📊 执行记录类', items: ['数据标准发布与执行记录', '数据质量检查与整改记录', '数据安全审计报告', '数据需求管理台帐'] },
        { category: '🛠️ 工具平台类', items: ['数据标准管理平台截图', '数据质量监控平台截图', '元数据管理平台截图', '数据安全管控平台截图'] }
      ],
      assessmentItems: [
        { domain: '数据战略', point: '战略规划与执行', level1: '无规划', level3: '有分年度实施计划', level5: '动态调整、创新引领' },
        { domain: '数据标准', point: '标准覆盖范围', level1: '无标准', level3: '覆盖核心业务域', level5: '全域覆盖、自动校验' },
        { domain: '数据质量', point: '质量监控机制', level1: '人工抽检', level3: '自动化规则检查', level5: '实时监控、智能修复' },
        { domain: '数据安全', point: '安全管控水平', level1: '基础防护', level3: '分级分类管控', level5: '智能风控、态势感知' }
      ],
      cases: [
        {
          name: '某能源央企', industry: '能源电力行业', level: '4', color: 'linear-gradient(135deg, #10B981, #059669)',
          duration: '14个月', team: '20人核心团队',
          practices: ['成立CDO领衔的数据治理委员会', '建设统一数据标准管理平台', '梳理2000+数据标准、500+业务术语', '实施数据质量六维度自动化监控'],
          result: '数据标准覆盖率从30%提升至92%，数据质量问题工单处理效率提升68%。'
        },
        {
          name: '某大型银行', industry: '金融行业', level: '4', color: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          duration: '12个月', team: '35人核心团队',
          practices: ['构建三道防线数据治理架构', '元数据驱动的数据血缘管理', '建立数据质量KPI考核体系', '全行级数据资产目录'],
          result: '监管报送数据准确率达99.97%，DCMM 4级全能力域达标。'
        },
        {
          name: '某制造集团', industry: '装备制造行业', level: '3', color: 'linear-gradient(135deg, #F59E0B, #D97706)',
          duration: '10个月', team: '12人核心团队',
          practices: ['物料主数据统一编码（20万+SKU）', '供应商主数据治理', '建设数据质量管理平台', '分阶段推进数据管理制度落地'],
          result: '首次参评即达3级，主数据一致性从52%提升至89%。'
        }
      ],
      roadmap: [
        { stage: '启动', title: '项目启动与现状诊断', desc: '明确认证目标等级，对标评估现状，输出差距分析报告', tasks: ['成立认证工作组', '选定咨询机构', 'DCMM自评', '差距报告'], duration: '1-2个月', color: '#4A90D9' },
        { stage: '建设', title: '体系建设与工具落地', desc: '补齐制度规范，建设或完善数据管理工具平台', tasks: ['制度编写', '标准梳理', '平台部署', '试点验证'], duration: '3-6个月', color: '#1F6FEB' },
        { stage: '运营', title: '体系运营与证据积累', desc: '让制度和流程真正运转起来，积累执行证据', tasks: ['日常运营', '质量检查', '会议记录', '整改闭环'], duration: '3-6个月', color: '#0E4EAD' },
        { stage: '申报', title: '材料整理与正式评审', desc: '整理全部证据材料，申报评审并配合现场检查', tasks: ['材料整理', '模拟评审', '正式申报', '现场配合'], duration: '2-3个月', color: '#0E1F52' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }
.article-meta i { margin-right: 4px; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { color: #0E1F52; text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.6rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 成熟度 */
.maturity-levels { margin: 24px 0; }
.maturity-card { display: flex; gap: 16px; align-items: flex-start; background: #F6F8FC; border-radius: 12px; padding: 20px; margin-bottom: 12px; border-left: 4px solid; border: 1px solid #E8ECF0; }
.maturity-level-badge { color: #fff; padding: 6px 14px; border-radius: 8px; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.maturity-info h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.maturity-desc { font-size: 14px; color: #5A5E66; margin-bottom: 8px; }
.maturity-features { display: flex; gap: 8px; flex-wrap: wrap; }
.maturity-feature { background: #E8ECF0; padding: 3px 10px; border-radius: 6px; font-size: 12px; color: #3D4150; }

/* 能力域 */
.domain-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.domain-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; transition: all 0.3s; }
.domain-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.domain-icon { font-size: 2rem; margin-bottom: 8px; }
.domain-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.domain-desc { font-size: 13px; color: #5A5E66; margin-bottom: 10px; }
.domain-items { display: flex; flex-direction: column; gap: 4px; }
.domain-items span { font-size: 12px; color: #1A73E8; padding: 2px 0; }
.domain-items span::before { content: '▸ '; }

/* 流程 */
.process-flow { position: relative; padding-left: 40px; margin: 24px 0; }
.process-flow::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #059669); border-radius: 2px; }
.process-step { position: relative; margin-bottom: 20px; }
.process-step:last-child { margin-bottom: 0; }
.process-number { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.process-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.process-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.process-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.process-duration { font-size: 13px; color: #1A73E8; font-weight: 600; }

/* 材料 */
.material-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.material-category { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.material-category h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.material-category ul { list-style: none; padding: 0; margin: 0; }
.material-category li { font-size: 13px; color: #5A5E66; padding: 4px 0 4px 14px; position: relative; }
.material-category li::before { content: '✓'; position: absolute; left: 0; color: #10B981; font-weight: 700; }

/* 评估表 */
.assessment-table { margin: 24px 0; overflow-x: auto; }
.assessment-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.assessment-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.assessment-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.assessment-table tr:nth-child(even) td { background: #F6F8FC; }
.domain-cell { font-weight: 700; color: #0E1F52; }

/* 工具 */
.tool-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.tool-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; transition: all 0.3s; }
.tool-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.tool-icon { font-size: 2rem; margin-bottom: 8px; }
.tool-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.tool-card p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin: 0; }

/* 案例 */
.case-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.case-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.case-header { padding: 24px; color: #fff; }
.case-level { font-size: 13px; font-weight: 700; opacity: 0.9; margin-bottom: 8px; }
.case-header h3 { font-size: 18px; font-weight: 800; margin-bottom: 4px; }
.case-industry { font-size: 13px; opacity: 0.8; margin: 0; }
.case-body { padding: 20px; }
.case-metric { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #E8ECF0; }
.metric-label { color: #5A5E66; }
.metric-value { color: #0E1F52; font-weight: 600; }
.case-body h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin: 12px 0 8px; }
.case-body ul { list-style: none; padding: 0; margin: 0 0 12px; }
.case-body li { font-size: 13px; color: #5A5E66; padding: 2px 0 2px 14px; position: relative; }
.case-body li::before { content: '▸'; position: absolute; left: 0; color: #1A73E8; }
.case-result { font-size: 13px; color: #10B981; font-weight: 600; line-height: 1.5; margin: 0; }

/* 路线图 */
.roadmap { position: relative; padding-left: 50px; margin: 24px 0; }
.roadmap::before { content: ''; position: absolute; left: 24px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.roadmap-phase { position: relative; margin-bottom: 24px; }
.roadmap-phase:last-child { margin-bottom: 0; }
.roadmap-marker { position: absolute; left: -50px; width: 48px; height: 28px; border-radius: 6px; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; z-index: 1; }
.roadmap-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.roadmap-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.roadmap-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 10px; }
.roadmap-tasks { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.roadmap-tasks span { background: #EBF3FE; color: #1A73E8; padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 500; }
.roadmap-duration { font-size: 13px; color: #1A73E8; font-weight: 600; }

/* 服务 */
.service-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.service-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; text-align: center; transition: all 0.3s; }
.service-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.service-icon { font-size: 2rem; margin-bottom: 12px; }
.service-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.service-card p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin: 0; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .domain-grid { grid-template-columns: repeat(2, 1fr); }
  .tool-grid { grid-template-columns: repeat(2, 1fr); }
  .case-cards { grid-template-columns: 1fr; }
  .material-list { grid-template-columns: 1fr; }
  .service-grid { grid-template-columns: repeat(2, 1fr); }
  .article-meta { flex-wrap: wrap; justify-content: center; }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .domain-grid { grid-template-columns: 1fr; }
  .tool-grid { grid-template-columns: 1fr; }
  .service-grid { grid-template-columns: 1fr; }
}
</style>
